import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialRequisitionItemFormResponse } from '@Types/api/form';
import { MInput, MSkeleton } from '@Components/Base';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { useAppSelector } from '@Hooks/index';

import { RequestListSearchResponse } from '@Types/api/requestList';
import {
  useGetRequisitionCodeDescItemsMutation,
  useGetRequisitionItemsMutation,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';
import MaterialTabItem from './MaterialTabItem';

import styles from '../view-tab.module.css';
import { findMaterialItemBySearchValue } from '../../../../../utils/finder';

const MaterialsTab: React.FC = () => {
  const reqId = useAppSelector(getModalRecordKey);
  const [getRequisitionCodeDescItems] =
    useGetRequisitionCodeDescItemsMutation();
  const [getRequisitionItems, { isLoading, isSuccess }] =
    useGetRequisitionItemsMutation();
  const [listItems, setListItems] =
    useState<MaterialRequisitionItemFormResponse[]>();
  const [searchedMatItems, setSearchedMatItems] =
    useState<Array<MaterialRequisitionItemFormResponse>>();
  const [requisitionItems, setRequisitionItems] =
    useState<RequestListSearchResponse<MaterialRequisitionItemFormResponse>>();

  useEffect(() => {
    if (reqId) {
      getRequisitionItems({ requisitionId: Number(reqId) })
        .unwrap()
        .then((response) => {
          setRequisitionItems(response);
        });
    }
  }, [reqId]);

  useEffect(() => {
    if (requisitionItems?.content?.length && !isLoading) {
      getRequisitionCodeDescItems({ reqId: Number(reqId) })
        .unwrap()
        .then((descriptionData) => {
          const mappedData = requisitionItems?.content?.map(
            (requisitionItem) => {
              const findDescriptionItem = descriptionData.find(
                (itemDesc) => itemDesc.reqItemId === requisitionItem.reqItemId
              );

              return findDescriptionItem
                ? {
                    ...requisitionItem,
                    actName: findDescriptionItem.data.actName,
                    resDesc: findDescriptionItem.data.resDesc,
                    matDesc: findDescriptionItem.data.matDesc,
                    plantCode: findDescriptionItem.data.plantCode,
                    plantName: findDescriptionItem.data.plantName,
                    ledgerName: findDescriptionItem.data.ledgerName,
                  }
                : requisitionItem;
            }
          );

          setListItems(mappedData);
        });
    }
  }, [requisitionItems, isLoading]);

  const handleSearchMaterialItems = useCallback(
    (value: string) => {
      if (listItems?.length) {
        const result = findMaterialItemBySearchValue(listItems, value);
        setSearchedMatItems(result);
      }
    },
    [listItems]
  );

  const listMatItems = useMemo(() => {
    if (!searchedMatItems?.length) {
      return listItems;
    }
    return searchedMatItems;
  }, [searchedMatItems, listItems]);

  return (
    <div className={styles.viewTabMaterials}>
      <MInput
        className={styles.search}
        searchInput
        placeholder="Search..."
        onChange={(event) => handleSearchMaterialItems(event.target.value)}
        disabled={!isSuccess}
      />
      {isSuccess ? (
        <div className={styles.container}>
          {listMatItems &&
            listMatItems.map((matItem) => (
              <MaterialTabItem
                index={matItem.reqItemId}
                activityCode={matItem.actCode}
                activityName={matItem.actName}
                materialCode={matItem.matCode}
                materialDescription={matItem.matDesc}
                plantName={matItem.plantName}
                plantCode={matItem.plantCode}
                plantId={matItem.plantId}
                ledgerName={matItem.ledgerName}
                ledgerCode={matItem.ledgerCode}
                ledgerId={matItem.ledgerId}
                qty={matItem.reqQty}
                remarks={matItem.transferRemarks}
                resourceCode={matItem.resCode}
                resourceDescription={matItem.resDesc}
                unit={matItem.unitName}
                repeatItem={matItem.repeatForAll}
                key={matItem.reqItemId}
              />
            ))}
        </div>
      ) : (
        <div className={styles.container}>
          <MSkeleton repeat={2} className="skeleton-item" />
        </div>
      )}
    </div>
  );
};

export default MaterialsTab;
